// src/components/resources/tools/generators/AiChat/Icons.tsx

import type { JSX, SVGProps } from 'react'

/**
 * Minimal stroke-icon set for the assistant action toolbars (copy, feedback,
 * regenerate, read-aloud, share, edit) and composer affordances. Drawn inline
 * so they rasterise through html2canvas without a sprite fetch; all inherit
 * `currentColor` and a 1.6px stroke tuned to match the host UIs at 16px.
 */
type IconProps = SVGProps<SVGSVGElement>

function base(props: IconProps): IconProps {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...props,
  }
}

export function CopyIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <rect x="9" y="9" width="11" height="11" rx="2.2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h8" />
    </svg>
  )
}

export function ThumbUpIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z" />
      <path d="M7 11l4.2-8a2 2 0 0 1 2.7-.8c.7.4 1 1.2.8 2L13.8 9H20a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 18.8 20H7" />
    </svg>
  )
}

export function ThumbDownIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z" />
      <path d="M17 13l-4.2 8a2 2 0 0 1-2.7.8c-.7-.4-1-1.2-.8-2L10.2 15H4a2 2 0 0 1-2-2.3l1.2-7A2 2 0 0 1 5.2 4H17" />
    </svg>
  )
}

export function RefreshIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 4v5h-5" />
    </svg>
  )
}

export function VolumeIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M11 5L6 9H3v6h3l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 6a8.5 8.5 0 0 1 0 12" />
    </svg>
  )
}

export function ShareIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
      <path d="M16 6l-4-4-4 4" />
      <path d="M12 2v13" />
    </svg>
  )
}

export function EditIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  )
}

export function MoreIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function ChevronDownIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

export function PlusIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function MicIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  )
}

export function SendArrowIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base(props)}>
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  )
}
